import type { AppContext, AppProps, NextWebVitalsMetric } from 'next/app'
import { appWithTranslation } from 'next-i18next'
import { ConsentProvider } from 'utils/hooks/useConsentManager'

import { ErrorBoundary } from 'modules/utils/ErrorBoundary'
import { start } from 'utils/bugsnag'
import nextI18NextConfig from '../../next-i18next.config'
import App from 'next/app'

import { InternalAnalyticsProvider } from 'utils/internalAnalytics'

// TODO: CL-47388: Remove logic with mparticle feature flag
const IsMParticleEnabledSwitch = ({
  airgapSrcUrl,
  appEnv,
  mParticleApiKey,
  isMParticleEnabled,
  children,
  nonce,
}: {
  airgapSrcUrl: string
  appEnv?: string
  mParticleApiKey?: string
  isMParticleEnabled: boolean
  children: React.ReactNode
  nonce: string
}) => {
  return isMParticleEnabled && mParticleApiKey && appEnv ? (
    <InternalAnalyticsProvider appEnv={appEnv} mParticleApiKey={mParticleApiKey}>
      {children}
    </InternalAnalyticsProvider>
  ) : (
    <ConsentProvider airgapSrc={airgapSrcUrl} nonce={nonce}>
      {children}
    </ConsentProvider>
  )
}

const isDevEnv =
  process.env.NEXT_PUBLIC_APP_ENV === 'local' || process.env.NEXT_PUBLIC_APP_APP_ENV === 'dev'

export function reportWebVitals(metric: NextWebVitalsMetric) {
  isDevEnv && console.info(metric)
}

const airgapSrc =
  process.env.APP_ENV === 'prod'
    ? 'https://transcend-cdn.com/cm/d1a4d701-2ca2-487c-9b90-59558395eb44/airgap.js'
    : 'https://transcend-cdn.com/cm-test/d1a4d701-2ca2-487c-9b90-59558395eb44/airgap.js'

// Initialize Bugsnag
start()

type MyAppProps = AppProps & { nonce: string }

const MyApp = ({ Component, pageProps, nonce }: MyAppProps) => {
  return (
    <IsMParticleEnabledSwitch
      airgapSrcUrl={airgapSrc}
      appEnv={process.env.APP_ENV}
      mParticleApiKey={process.env.NEXT_PUBLIC_MPARTICLE_API_KEY}
      isMParticleEnabled={process.env.NEXT_PUBLIC_IS_MPARTICLE_ENABLED === 'true'}
      nonce={nonce}
    >
      <ErrorBoundary>
        <Component {...pageProps} />
      </ErrorBoundary>
    </IsMParticleEnabledSwitch>
  )
}

MyApp.getInitialProps = async (context: AppContext) => {
  const ctx = await App.getInitialProps(context)

  const nonce = context.ctx.req?.headers['x-nonce']

  return { ...ctx, nonce }
}

export default appWithTranslation(MyApp, nextI18NextConfig)
